import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/days";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { formatCZK, formatDate, currencySymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { payableTotal } from "@/lib/documents";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";
import { autoPill } from "@/components/shared/Pill";

const METHODS = ["Převodem", "Hotově", "Kartou"];
const today = todayISO;   // 401: místní den, ne UTC

// Seznam úhrad jednoho dokladu — vytažený z dialogu (157/B), ať ho může použít
// i „Kontrola dokladu" (DocumentReviewDialog). Řádek: datum · způsob · poznámka
// + částka a koš; po smazání (s potvrzením) se vrací aktualizovaný doklad.
// Mobil: dva řádky (datum+způsob / částka+koš), desktop jeden řádek.
export function PaymentsList({ doc, direction = "out", onChanged, compact }) {
  const base = direction === "in" ? "/issued-invoices" : "/documents";
  const payments = doc?.payments || [];
  const del = async (p) => {
    if (!await ask(`Smazat úhradu ${formatCZK(p.amount)} z ${formatDate(p.date)}?`)) return;
    try { const { data } = await api.delete(`${base}/${doc.id}/payments/${p.id}`); onChanged?.(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!payments.length) return <p className="text-sm text-muted-foreground">{direction === "in" ? "Zatím žádný příjem." : "Zatím žádné platby."}</p>;
  return (
    <div className="space-y-2" data-testid="payments-list-rows">
      {payments.map((p) => (
        <div key={p.id} data-testid={`payment-${p.id}`}
          className={`flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-0.5 bg-secondary rounded-[10px] ${compact ? "p-2" : "p-2.5"} text-sm`}>
          <span className="w-full sm:w-auto min-w-0 truncate text-muted-foreground">
            <span className="tabular-nums text-foreground">{formatDate(p.date)}</span>
            {p.method ? ` · ${p.method}` : ""}{p.note ? ` · ${p.note}` : ""}
          </span>
          <b className="tabular-nums sm:ml-auto">{formatCZK(p.amount)}</b>
          <button onClick={() => del(p)} className="text-muted-foreground hover:text-destructive shrink-0 p-1" data-testid={`del-payment-${p.id}`}><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
}

// Dialog úhrad jedné faktury. Sdílený mezi Doklady a kartou Platby, aby
// existoval JEDEN tok zápisu platby.
//
// Úhrady patří k DOKLADU (`doc.payments`), ne k dodavateli — v prototypu se
// filtrovalo podle dodavatele a dialog pak ukazoval i platby jiných faktur
// téhož dodavatele, takže si čísla protiřečila.
export function PaymentsDialog({ doc, onClose, onChanged, direction = "out" }) {
  const [form, setForm] = useState({ date: today(), amount: "", method: METHODS[0], note: "" });
  const [busy, guard] = useSubmitGuard();   // dvojklik nesmí přidat platbu dvakrát

  if (!doc) return null;
  // Vydaná faktura (direction="in") jede na vlastní endpoint a mluví o příjmu.
  const inc = direction === "in";
  const base = inc ? "/issued-invoices" : "/documents";
  const total = payableTotal(doc);   // konečná faktura: jen doplatek po zálohách (171); 401: i ručně opsané K úhradě
  const paid = (doc.payments || []).reduce((s, p) => s + (p.amount || 0), 0);
  const rest = Math.max(total - paid, 0);

  const add = async () => {
    const amount = Number(String(form.amount).replace(",", "."));
    if (!amount || amount <= 0) return toast.error("Zadejte částku.");
    // Server je autorita, ale přeplatek zastavíme už tady (132/B).
    if (amount > rest + 0.5) return toast.error(`Platba přesahuje zbývající částku — zbývá ${formatCZK(rest)}.`);
    try {
      const { data } = await api.post(`${base}/${doc.id}/payments`, {
        date: form.date, amount, method: form.method || null, note: form.note || null });
      setForm({ date: today(), amount: "", method: METHODS[0], note: "" });
      toast.success(data.payment_status === "Zaplaceno"
        ? (inc ? "Faktura je plně inkasovaná." : "Faktura je plně uhrazena.")
        : (inc ? "Příjem zapsán." : "Platba přidána."));
      onChanged?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="payments-dialog">
        <DialogHeader>
          <DialogTitle>{inc ? "Platby vydané faktury" : "Platby faktury"}</DialogTitle>
          <DialogDescription>
            {(inc ? doc.client_name : doc.supplier_name) || "Bez názvu"}{doc.invoice_number ? ` · č. ${doc.invoice_number}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-secondary rounded-[14px] p-3"><div className="text-[11px] text-muted-foreground uppercase tracking-widest">{doc.advances_deducted_gross > 0 || (doc.amount_due != null && doc.amount_due !== "") ? "K úhradě" : "Celkem"}</div><div className="font-heading font-extrabold tabular-nums">{formatCZK(total)}</div></div>
            <div className="bg-secondary rounded-[14px] p-3"><div className="text-[11px] text-muted-foreground uppercase tracking-widest">{inc ? "Přijato" : "Uhrazeno"}</div><div className="font-heading font-extrabold tabular-nums text-success" data-testid="paid-total">{formatCZK(paid)}</div></div>
            <div className="bg-secondary rounded-[14px] p-3"><div className="text-[11px] text-muted-foreground uppercase tracking-widest">Zbývá</div><div className="font-heading font-extrabold tabular-nums text-brand">{formatCZK(rest)}</div></div>
          </div>

          <PaymentsList doc={doc} direction={direction} onChanged={onChanged} />

          <div className="border-t border-border pt-3 space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>{inc ? "Datum příjmu" : "Datum úhrady"}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-10 mt-1" data-testid="pay-date" /></div>
              <div><Label>Částka ({currencySymbol()})</Label>
                <Input inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="h-10 mt-1 border-brand tabular-nums" data-testid="pay-amount" /></div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, amount: String(rest) })} className="h-9 rounded-full font-semibold" data-testid="pay-fill-rest">Celý zbytek</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, amount: String(Math.round(rest / 2)) })} className="h-9 rounded-full font-semibold" data-testid="pay-fill-half">Polovina</Button>
            </div>
            <div>
              <Label>Způsob</Label>
              <div ref={autoPill} className="flex gap-1 p-1 mt-1 rounded-full bg-secondary">
                {METHODS.map((m) => (
                  <button key={m} type="button" onClick={() => setForm({ ...form, method: m })} data-testid={`pay-method-${m}`} data-pill-on={form.method === m || undefined}
                    className={`flex-1 h-9 rounded-full text-sm font-bold transition-colors ${form.method === m ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <Input placeholder="Poznámka (nepovinné)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-10" data-testid="pay-note" />
            <Button onClick={() => guard(add)} disabled={busy} data-testid="add-payment-btn" className="w-full h-12 font-bold">
              <Plus size={16} /> {busy ? "Ukládám…" : inc ? "Zapsat příjem" : "Přidat platbu"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
